import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useIntl } from 'react-intl';

import { InGameCurrency } from '#src/__generated__/globalTypes.ts';
import { glossary } from '#src/lib/glossary.ts';
import { isType, withFragments } from '#src/lib/gql/index.ts';
import {
  isCraftingClue,
  isEnergyCurrency,
  isWheelTicket,
  isXPCurrency,
} from '#src/lib/rewards.tsx';
import { inGameCurrencyToRarity, scarcityMessages } from '#src/lib/scarcity.ts';

import type { useProbableRewardConfigSummary_ProbableRewardConfig } from './__generated__/useProbableRewardConfigSummary.graphql.ts';

export const useProbableRewardConfigSummary = withFragments(
  (
    probableRewardConfigs: useProbableRewardConfigSummary_ProbableRewardConfig[]
  ) => {
    const { formatMessage } = useIntl();

    return probableRewardConfigs.map(probableRewardConfig =>
      probableRewardConfig.rewardConfigs.map(config => {
        if (isType(config, 'ShopItemRewardConfig')) {
          return {
            name: formatMessage(glossary.determinative, {
              type: config.shopItem.name,
            }),
          };
        }
        if (isType(config, 'CardPacksRewardConfig')) {
          return {
            name: formatMessage(glossary.determinativeCardPack),
          };
        }
        if (isType(config, 'CardRewardConfig')) {
          return {
            name: formatMessage(glossary.determinativeCard),
          };
        }
        if (isType(config, 'CardShardRewardConfig')) {
          return {
            name: formatMessage(glossary.determinativeEssence),
          };
        }
        if (isType(config, 'CoinRewardConfig')) {
          return {
            name: `${config.currency}`,
          };
        }
        if (isType(config, 'ConversionCreditRewardConfig')) {
          return {
            name: formatMessage(glossary.determinativeMarketCredit),
          };
        }
        if (isType(config, 'DeliverableItemRewardConfig')) {
          return {
            name: formatMessage(glossary.determinative, {
              type: config.deliverableItem.name,
            }),
          };
        }
        if (isType(config, 'ExperienceRewardConfig')) {
          return {
            name: formatMessage(glossary.determinativeExperiences),
          };
        }
        if (isType(config, 'InGameCurrencyRewardConfig')) {
          if (isXPCurrency(config.currency)) {
            const rarity = inGameCurrencyToRarity[config.currency]!;
            return {
              name: formatMessage(
                {
                  defaultMessage: '{amount} {rarity} XP',
                },
                {
                  amount: config.amount,
                  rarity: formatMessage(scarcityMessages[rarity]),
                }
              ),
            };
          }
          if (isEnergyCurrency(config.currency)) {
            const rarity = inGameCurrencyToRarity[config.currency]!;
            return {
              name: formatMessage(
                {
                  defaultMessage: '{amount} {rarity} Energy',
                },
                {
                  amount: config.amount,
                  rarity: formatMessage(scarcityMessages[rarity]),
                }
              ),
            };
          }
          if (isCraftingClue(config.currency)) {
            return {
              name: formatMessage({
                defaultMessage: 'crafting clue',
              }),
            };
          }
          if (isWheelTicket(config.currency)) {
            return {
              name: formatMessage(glossary.determinativeTickets),
            };
          }
          if (config.currency === InGameCurrency.COMMON_GEM) {
            return {
              name: formatMessage(glossary.determinativeGems),
            };
          }
          if (config.currency === InGameCurrency.OLD_CLUB_SHOP_COIN) {
            return {
              name: 'coins',
            };
          }
          return { name: 'rewards' };
        }
        if (isType(config, 'MonetaryRewardConfig')) {
          return {
            name: formatMessage(glossary.determinativeMoney),
          };
        }
        if (isType(config, 'ProbabilisticBundleRewardConfig')) {
          return {
            name: formatMessage(glossary.determinativeBox),
          };
        }
        // eslint-disable-next-line no-console
        console.error('Unknown reward', config);
        return {
          name: 'Reward',
        };
      })
    );
  },
  {
    ProbableRewardConfig: gql`
      fragment useProbableRewardConfigSummary_ProbableRewardConfig on ProbableRewardConfig {
        id
        rewardConfigs {
          id
          ... on ShopItemRewardConfig {
            id
            shopItem {
              id
              name
            }
          }
          ... on CardPacksRewardConfig {
            id
          }
          ... on CardRewardConfig {
            id
          }
          ... on CardShardRewardConfig {
            id
          }

          ... on CoinRewardConfig {
            id
            amount
            currency
          }

          ... on ConversionCreditRewardConfig {
            id
          }

          ... on DeliverableItemRewardConfig {
            id
            deliverableItem {
              slug
              name
            }
          }
          ... on ExperienceRewardConfig {
            id
          }
          ... on InGameCurrencyRewardConfig {
            id
            amount
            currency
          }
          ... on MonetaryRewardConfig {
            id
          }
          ... on ProbabilisticBundleRewardConfig {
            id
          }
        }
      }
    ` as TypedDocumentNode<useProbableRewardConfigSummary_ProbableRewardConfig>,
  }
);
